import { Box, Button, Divider, InputAdornment, ListSubheader, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useMemo, useState } from "react";
import type { InvoiceDiscountApplyToOption } from "./InvoiceDiscountsCard";
import type { InvoiceServiceFeeDraft } from "./InvoiceServiceFeesCard";

export const SERVICE_FEE_PRESETS = [
  "Service fee",
  "Processing fee",
  "Travel fee",
  "Sales tax",
  "Late payment fee",
  "Rush service fee",
  "Cancellation fee",
  "Administrative fee",
] as const;

export const CUSTOM_FEE_OPTION = "__custom__";

export function mapPresetToFeeType(preset: string): string {
  switch (preset) {
    case "Processing fee":
      return "credit_card_surcharge";
    case "Travel fee":
      return "travel";
    case "Service fee":
      return "mandatory_service_charge";
    case "Sales tax":
      return "sales_tax";
    case "Late payment fee":
    case "Rush service fee":
    case "Cancellation fee":
    case "Administrative fee":
    case CUSTOM_FEE_OPTION:
    default:
      return "other";
  }
}

interface ServiceFeeModalFormProps {
  initialValues?: InvoiceServiceFeeDraft;
  defaultLabel?: string;
  applyToOptions?: InvoiceDiscountApplyToOption[];
  onSave: (data: Omit<InvoiceServiceFeeDraft, "id">) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

export function ServiceFeeModalForm({
  initialValues,
  defaultLabel = "Service fee",
  applyToOptions = [],
  onSave,
  onCancel,
  isEdit = false,
}: ServiceFeeModalFormProps) {
  const initialLabel = initialValues?.label ?? defaultLabel;
  const isInitialPreset = SERVICE_FEE_PRESETS.includes(initialLabel as any);

  const [feeType, setFeeType] = useState<string>(isInitialPreset ? initialLabel : CUSTOM_FEE_OPTION);
  const [customLabel, setCustomLabel] = useState(isInitialPreset ? "" : initialLabel);
  const [kind, setKind] = useState<"amount" | "percentage">(initialValues?.kind ?? "amount");
  const [value, setValue] = useState(initialValues?.value ?? "");
  const [appliesToKey, setAppliesToKey] = useState(initialValues?.appliesToKey ?? "invoice");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const effectiveLabel = feeType === CUSTOM_FEE_OPTION ? customLabel.trim() : feeType;
  const parsedValue = Number.parseFloat(String(value || "0"));
  const hasValue = Number.isFinite(parsedValue) && parsedValue >= 0 && value.trim().length > 0;
  const hasValidLabel = effectiveLabel.length > 0;

  const labelError = hasSubmitted && !hasValidLabel;
  const valueError = hasSubmitted && !hasValue;

  const groupedOptions = useMemo(() => {
    const general: InvoiceDiscountApplyToOption[] = [];
    const services: InvoiceDiscountApplyToOption[] = [];

    applyToOptions.forEach((opt) => {
      if (opt.type === "invoice" || opt.key === "invoice") {
        general.push(opt);
      } else if (opt.type === "service" || opt.eventUid != null) {
        services.push(opt);
      }
      // Note: extra_line_item (Other Services) is excluded for service fees
    });

    return { general, services };
  }, [applyToOptions]);

  const handleFeeTypeChange = (newType: string) => {
    setFeeType(newType);
  };

  const handleSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setHasSubmitted(true);
    if (!hasValidLabel || !hasValue) return;

    const option = applyToOptions.find((opt) => opt.key === appliesToKey);
    const resolvedType = option?.type === "extra_line_item" ? "invoice" : (option?.type ?? "invoice");
    onSave({
      label: effectiveLabel,
      feeType: mapPresetToFeeType(feeType),
      kind,
      value: String(parsedValue),
      appliesToType: resolvedType,
      appliesToKey: resolvedType === "invoice" ? "invoice" : appliesToKey,
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Preset Selector */}
        <TextField size="small" select label="Fee type" value={feeType} onChange={(e) => handleFeeTypeChange(e.target.value)} fullWidth>
          {SERVICE_FEE_PRESETS.map((preset) => (
            <MenuItem key={preset} value={preset}>
              {preset}
            </MenuItem>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem value={CUSTOM_FEE_OPTION}>Custom...</MenuItem>
        </TextField>

        {/* Custom Label Input when Custom is selected */}
        {feeType === CUSTOM_FEE_OPTION && (
          <TextField
            size="small"
            label="Custom fee name"
            placeholder="e.g. Equipment fee, Disposal fee"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            fullWidth
            required
            autoFocus
            error={labelError}
            helperText={labelError ? "Fee name is required." : undefined}
          />
        )}

        {applyToOptions.length > 1 && (
          <TextField size="small" select label="Applies to" value={appliesToKey} onChange={(e) => setAppliesToKey(e.target.value)} fullWidth>
            {groupedOptions.general.length > 0 && <ListSubheader sx={{ fontWeight: 700, lineHeight: "32px" }}>General</ListSubheader>}
            {groupedOptions.general.map((option) => (
              <MenuItem key={option.key} value={option.key} sx={{ pl: 3 }}>
                {option.label}
              </MenuItem>
            ))}

            {groupedOptions.services.length > 0 && <ListSubheader sx={{ fontWeight: 700, lineHeight: "32px" }}>Main Services / Visits</ListSubheader>}
            {groupedOptions.services.map((option) => (
              <MenuItem key={option.key} value={option.key} sx={{ pl: 3 }}>
                {option.label}
              </MenuItem>
            ))}


          </TextField>
        )}

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={kind}
            onChange={(_, val) => {
              if (val) setKind(val);
            }}
            sx={{
              height: 40,
              "& .MuiToggleButton-root": { px: 2, fontSize: "0.875rem", fontWeight: 700 },
            }}
          >
            <ToggleButton value="amount">$</ToggleButton>
            <ToggleButton value="percentage">%</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            label={kind === "percentage" ? "Percentage" : "Amount"}
            type="number"
            placeholder="0.00"
            inputProps={{
              min: 0,
              step: "0.01",
              max: kind === "percentage" ? 100 : undefined,
            }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            fullWidth
            required
            error={valueError}
            helperText={valueError ? "Enter a valid value." : undefined}
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{kind === "percentage" ? "%" : "$"}</InputAdornment>,
            }}
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Button variant="outlined" color="inherit" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" onClick={handleSubmit} sx={{ textTransform: "none", fontWeight: 700 }}>
          {isEdit ? "Save changes" : "Add service fee"}
        </Button>
      </Stack>
    </Box>
  );
}
